import React, { useMemo, useRef } from 'react';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import {
  echartsContent,
  mathFormat,
  transChannel,
} from '@/pages/opinion/utils';
import {
  baseColors,
  channelColorMap,
  extendColors,
  NO_DATE,
} from '@/pages/opinion/constants';
import {
  formatChartParamsDate,
  imgShowElement,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { formatTimeXAxisText } from '@/pages/opinion/utils/chart';
import { DBO_ARR, DBO_EC, DBO_V } from '@op/utils/format';
import { specialSecimalFormatPercentage } from '@op/utils/value';
import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';

export default function Chart({
  statistics,
  chartType = 'bar',
  type = 'counts',
  loading = true,
  keepParams = {},
  engagementTypeText,
  engagementType = 'engagements',
}: any) {
  const { formatMessage } = useIntl();
  const isEngagementRate = engagementType === 'engagement_per_view';

  const chartMap: any = {
    counts: {
      tooltip: 'Videos',
    },
    engagements: {
      tooltip: 'Engagement',
    },
    views: {
      tooltip: 'Views',
    },
  };

  let { tooltip } = chartMap[type];

  if (type === 'engagements') tooltip = engagementTypeText;

  const defaultColorArr = [...baseColors, ...extendColors];

  const xAxisIntervalLimit = 8;

  const showTotalLabel = (totals: number[] = [], clientWidth: number = 0) => {
    return totals.length <= 8 && chartType === 'bar' && clientWidth > 400;
  };
  const chartRef = useRef(null);

  const makeupOptions = () => {
    let legendData: {
      name: string;
      itemStyle: {
        color: string;
      };
    }[] = [];
    let yAxisAllKeys: string[] = [];
    let xAxisData: string[] = [];
    let seriesData: {
      itemStyle?: any;
      barMaxWidth?: string;
      barGap?: string;
      label?: any;
      name: string;
      type: string;
      stack?: string;
      data: (number | null)[];
    }[] = [];

    let sortMap = {};

    for (let index = 0; index < statistics.length; index += 1) {
      const curItem = statistics[index];
      const { y_axis, x_axis = '' } = curItem;

      let yAxisKeys: string[] = [];

      if (y_axis) {
        yAxisKeys = Object.keys(y_axis);
      }

      xAxisData.push(x_axis);
      yAxisAllKeys.push(...yAxisKeys);

      // 确定排序
      if (index === 0) {
        sortMap = { ...y_axis };
      }
    }
    yAxisAllKeys = Array.from(new Set(yAxisAllKeys));

    yAxisAllKeys.sort(DBO_ARR.sortByMap(sortMap, (item) => item, 'desc'));

    let recordColorNums = 0;

    const getColor = (item: any) => {
      let curColor = channelColorMap[item];
      let hasColor = true;
      if (!curColor) {
        curColor = defaultColorArr[recordColorNums];
        recordColorNums += 1;
        hasColor = false;
      }
      return curColor;
    };

    seriesData = yAxisAllKeys.map((item) => {
      return {
        name: item,
        type: chartType,
        stack: chartType === 'bar' ? 'video' : '',
        data: [],
        itemStyle: {
          color: getColor(item),
        },
        barMaxWidth: '70px',
      };
    });
    const totals: number[] = [];
    for (let index = 0; index < statistics.length; index += 1) {
      const curItem = statistics[index];
      const { y_axis } = curItem;

      let sum = 0;
      for (let index2 = 0; index2 < yAxisAllKeys.length; index2 += 1) {
        const curYAxisAllKeys = yAxisAllKeys[index2];
        const findItem = seriesData.find(
          (item) => item.name === curYAxisAllKeys,
        );

        if (typeof y_axis?.[curYAxisAllKeys] !== 'undefined') {
          if (findItem) {
            if (
              !isNaN(y_axis[curYAxisAllKeys]) &&
              y_axis[curYAxisAllKeys] >= 0
            ) {
              sum += y_axis[curYAxisAllKeys];
              findItem.data.push(y_axis[curYAxisAllKeys]);
            } else {
              findItem.data.push(null);
            }
          }
        } else {
          if (findItem) {
            findItem.data.push(null);
          }
        }
      }
      totals.push(sum);
    }
    if (chartType === 'bar' && !isEngagementRate) {
      seriesData.unshift({
        name: 'Sum',
        type: 'bar',
        barMaxWidth: '70px', // 柱子宽度同实体柱子，否则会错位
        barGap: '-100%', // 左移100%，stack不再与需堆叠的柱子在一列
        label: {
          normal: {
            show: showTotalLabel(totals, chartRef?.current?.clientWidth),
            position: 'top', // 位置设为top
            formatter: (c) => mathFormat(c?.value),
            textStyle: { color: '#444' },
          },
        },
        itemStyle: {
          normal: {
            color: 'rgba(128, 128, 128, 0)', // 为透明
          },
        },
        data: totals,
      });
    }

    recordColorNums = 0;
    legendData = yAxisAllKeys.map((item) => {
      return {
        name: item,
        itemStyle: {
          color: getColor(item),
        },
      };
    });
    const option = {
      // color: defaultColorArr,
      tooltip: {
        borderWidth: 0,
        padding: 0,
        confine: true,
        trigger: 'axis',
        extraCssText: 'z-index:8',
        formatter: (param: any[]) => {
          const { axisValue: sumKey, dataIndex } = param[0] || {};
          let timeText = '';
          if (keepParams?.date_type) {
            timeText = listTimeTitleFunc(sumKey, keepParams?.date_type);
          }

          return formatChartParamsDate({
            params: param,
            beforeSort: true,
            total: isEngagementRate ? undefined : totals[dataIndex],
            valueFormat: isEngagementRate
              ? (value: number) => specialSecimalFormatPercentage(value)
              : undefined,
            listTitleFunc: (item: any) => {
              const isNoOther = item.seriesName !== 'Others';
              const { img, name, flag } = channelInfo(item.seriesName);

              if (!flag) {
                return imgShowElement(
                  img,
                  isNoOther ? name : formatMessage({ id: item.seriesName }),
                );
              }
              return formatMessage({ id: item.seriesName || NO_DATE.EMPTY });
            },
            dealwithRecords(records, total) {
              const list = records
                .map((item) => {
                  if (isEngagementRate) return item;
                  return {
                    ...item,
                    percentage: DBO_V.percentage(item.originValue, total, 2),
                  };
                })
                .filter(
                  (item) => item.originText !== formatMessage({ id: 'Sum' }),
                );

              if (!isEngagementRate) {
                list.unshift({
                  text: formatMessage({ id: 'Sum' }),
                  value: DBO_V.reviews(total) as string,
                  percentage: total === 0 ? '-' : '100%',
                  originValue: total,
                  originText: 'Sum',
                });
              }

              return list;
            },
            tablesConfigFunc: (records: any[]) => {
              const columns = [
                {
                  header: timeText,
                  key: 'text',
                },
                {
                  header: formatMessage({
                    id: tooltip,
                  }),
                  weight: 'bold',
                  key: 'value',
                  align: 'right',
                },
              ];

              if (!isEngagementRate) {
                columns.push({
                  header: formatMessage({ id: 'Share-1' }),
                  weight: 'bold',
                  key: 'percentage',
                  align: 'right',
                });
              }

              return [
                {
                  showHeader: param.length > 1,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },
      legend: {
        data: legendData,
        formatter: (params: string) => {
          return `{${dealwithChannelChartKey(params)}|} {name|${transChannel(
            params,
          )}}`;
        },
      },
      grid: {
        top: '13%',
        left: '1%',
        right: '5%',
        bottom: '15%',
        containLabel: true,
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, keepParams?.date_type);
      }),
      xAxis: {
        type: 'category',
        boundaryGap: ['20%', 0],
        data: xAxisData,
        axisLabel: {
          interval: xAxisData?.length > xAxisIntervalLimit ? 'auto' : 0,
          fontSize: 10,
          showMaxLabel: true,
          formatter: (value: any) =>
            formatTimeXAxisText(value, keepParams?.date_type),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) =>
            isEngagementRate
              ? DBO_EC.reviewsPercent(value, true)
              : echartsContent(value),
          fontSize: 11,
        },
      },
      series: seriesData,
    };

    return option;
  };

  const { options, isEmpty } = useMemo(() => {
    const hasStatistics = Array.isArray(statistics) && statistics.length > 0;
    if (!hasStatistics) return { isEmpty: true, options: {} };
    const options = makeupOptions() || {};
    return { options, isEmpty: false };
  }, [statistics, chartType, formatMessage, engagementType, isEngagementRate]);

  return (
    <div ref={chartRef}>
      <EChartCommon
        options={options}
        loading={loading}
        empty={isEmpty}
        optionsProps={{ type: chartType }}
        height={type === 'engagements' ? 252 : 292}
      ></EChartCommon>
    </div>
  );
}
